import { useT } from '@/i18n/react';
import { SegmentedControl } from '@/components/ui';
import type { Visibility } from '@/types';
import './visibilityPicker.css';

/**
 * Everyone / Followers / Only me (docs/02 → Leállítás és mentés, Preferenciák).
 * Shared by the save form and the default setting so both read the same.
 */
export function VisibilityPicker({
  value,
  onChange,
  label,
  disabled = false,
}: {
  value: Visibility;
  onChange: (value: Visibility) => void;
  /** Visible group label; defaults to "Who can see it?". */
  label?: string;
  disabled?: boolean;
}) {
  const t = useT().activityVisibility;
  const hint = value === 'everyone' ? t.hintEveryone : value === 'followers' ? t.hintFollowers : t.hintOnlyMe;
  return (
    <fieldset className="visibility-picker" disabled={disabled}>
      <legend className="visibility-picker__label">{label ?? t.label}</legend>
      <SegmentedControl<Visibility>
        block
        label={label ?? t.label}
        value={value}
        onChange={onChange}
        options={[
          { value: 'everyone', label: t.everyone },
          { value: 'followers', label: t.followers },
          { value: 'only_me', label: t.onlyMe },
        ]}
      />
      <p className="visibility-picker__hint">{hint}</p>
    </fieldset>
  );
}

/**
 * " · Followers" / " · Only you" after a card's or detail's date line, so a
 * non-public activity is recognisable at a glance. Nothing for `everyone`
 * (the common case) or for a backend that does not report it yet.
 */
export function VisibilityNote({ value }: { value: Visibility | undefined }) {
  const t = useT().activityVisibility;
  if (value === undefined || value === 'everyone') return null;
  return <> · {value === 'followers' ? t.badgeFollowers : t.badgeOnlyMe}</>;
}
